<template>
  <div class="bg-white rounded-xl shadow-sm p-4">
    <div class="flex flex-col lg:flex-row lg:items-center gap-4">
      <!-- Loading -->
      <div v-if="loading" class="flex items-center justify-center w-full py-6">
        <i class="pi pi-spin pi-spinner text-2xl text-primary" />
      </div>

      <!-- Empty / unavailable -->
      <div v-else-if="!summary" class="flex items-center gap-3 w-full">
        <div class="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center shrink-0">
          <i class="pi pi-calendar-times text-gray-400 text-xl" />
        </div>
        <div>
          <div class="text-sm text-gray-500">ATTENDANCE</div>
          <div class="text-sm text-gray-400 mt-1">No records yet</div>
        </div>
        <Button label="Attendance" icon="pi pi-check-square" class="p-button-sm p-button-outlined ml-auto" @click="goToAttendance" />
      </div>

      <!-- Real data -->
      <template v-else>
        <div class="flex items-center gap-4">
          <DoughnutChart
            :labels="['Present', 'Absent']"
            :values="[summary.present, summary.absent]"
            :colors="['#2b5740', '#f6ad2b']"
          />
          <div>
            <div class="text-sm text-gray-500">ATTENDANCE</div>
            <div class="text-xl font-semibold mt-2">{{ summary.total }} day{{ summary.total === 1 ? '' : 's' }}</div>
            <div class="flex flex-wrap gap-x-4 gap-y-1 mt-3 text-xs">
              <span class="text-primary font-medium">PRST {{ presentPct }}%</span>
              <span class="text-amber-600 font-medium">ABST {{ absentPct }}%</span>
              <span v-if="summary.late > 0" class="text-orange-500 font-medium">LATE {{ latePct }}%</span>
            </div>
          </div>
        </div>

        <div class="flex items-center justify-between lg:ml-auto lg:flex-col lg:items-end gap-2">
          <div class="text-xs text-gray-500 lg:order-2">View / mark attendance</div>
          <Button label="Attendance" icon="pi pi-check-square" class="p-button-sm p-button-outlined" @click="goToAttendance" />
        </div>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useQuery } from '@vue/apollo-composable';
import { GET_STUDENT_ATTENDANCE } from '@/graphql';
import { useUserQuery } from '@/composables/queries/useUserQuery';
import DoughnutChart from '@/components/DashBoard/Home/StudentDough.vue';

const router = useRouter();
const { studentProfile, studentAcademicCalendar } = useUserQuery();

const { result, loading } = useQuery(
  GET_STUDENT_ATTENDANCE,
  () => ({
    student_id: studentProfile.value?.id,
    academic_calendar_id: studentAcademicCalendar.value?.academicCalendar?.id ?? null,
  }),
  () => ({
    enabled: !!studentProfile.value?.id,
    fetchPolicy: 'cache-and-network',
  }),
);

interface AttendanceSummary {
  present: number;
  absent: number;
  late: number;
  excused: number;
  total: number;
}

const summary = computed<AttendanceSummary | null>(() => {
  const s = result.value?.studentAttendance?.summary;
  if (!s || s.total === 0) return null;
  return s;
});

function pct(value: number): number {
  if (!summary.value || summary.value.total === 0) return 0;
  return Math.round((value / summary.value.total) * 100);
}

const presentPct = computed(() => pct(summary.value?.present ?? 0));
const absentPct = computed(() => pct(summary.value?.absent ?? 0));
const latePct = computed(() => pct(summary.value?.late ?? 0));

const goToAttendance = () => router.push({ name: 'student-attendance' });
</script>
